import React, { useState, useCallback } from 'react';
import { View } from 'react-native';
import { shape } from 'prop-types';
import { ModalHeader } from 'LocalGluons';
import { Device } from 'DeviceUtils';
import { t } from 'Locale';
import { RESET_TYPE } from 'Pages/VerificationCodePages/GetHelp/GetHelp';
import {
    SuccessPage,
    SendCodePin as SendCode,
    VerifyCodePin as VerifyCode,
    GetHelp,
    CodeSent
} from '../VerificationCodePages';
import defaultStyles from './ResetPin.jss';
import { SetPin } from './pages/SetPin';

/**
 * @typedef { import('react-navigation').NavigationScreenProp } Navigation
 *
 * @typedef {{ navigation: Navigation }} Props
 */

export const pages = Object.freeze({
    sendCode: 'sendCode',
    codeSent: 'codeSent',
    verifyCode: 'verifyCode',
    getHelp: 'getHelp',
    pinSuccess: 'pinSuccess',
    setPin: 'setPin'
});

/**
 * @param {Props} props
 */
const ResetPin = ({ navigation, route }) => {
    const initialRoute = route.params?.initialRoute || undefined;
    const nextRoute = route.params?.nextRoute || undefined;
    const nextRouteParams = route.params?.nextRouteParams || undefined;
    const [page, setPage] = useState(initialRoute || pages.sendCode);
    const [data, setData] = useState({});

    const resetPinFinishedCallback = useCallback(() => {
        if (nextRoute === 'SettingsParental') {
            if (Device.isTabletOrWeb) {
                navigation.navigate('settings', {
                    selectedOption: 'SettingsParental',
                    pin: data.newPin,
                    ...nextRouteParams
                });
            } else {
                navigation.navigate('SettingsParental', { pin: data.newPin, ...nextRouteParams });
            }
        } else if (nextRoute === 'Details') {
            navigation.navigate('Details', {
                id: nextRouteParams?.getId(),
                type: nextRouteParams?.modelType,
                platform: nextRouteParams?.data?.pckg?.platform
            });
        } else {
            navigation.pop();
        }
    }, [data.newPin]);

    const onCloseCallback = useCallback(() => {
        navigation.pop();
    }, []);

    /**
     * @param {string} newPage
     */
    const setPageFactory = (newPage) => (newData) => {
        setPage(newPage);
        if (newData) setData(newData);
    };

    const closeModal = () => {
        navigation.pop();
    };

    const pageComponents = {
        [pages.sendCode]: (
            <SendCode onSubmitted={setPageFactory(pages.codeSent)} onClose={closeModal} />
        ),
        [pages.codeSent]: (
            <CodeSent
                title={t('SETTINGS.PARENTAL_CONTROL_RESET_PIN')}
                onOk={setPageFactory(pages.verifyCode)}
            />
        ),
        [pages.verifyCode]: (
            <VerifyCode
                data={data}
                onSubmitted={setPageFactory(pages.setPin)}
                onGetHelp={setPageFactory(pages.getHelp)}
                Navigation={navigation}
            />
        ),
        [pages.getHelp]: (
            <GetHelp onSendAgain={setPageFactory(pages.sendCode)} type={RESET_TYPE.PIN} />
        ),
        [pages.setPin]: <SetPin data={data} onSubmitted={setPageFactory(pages.pinSuccess)} />,
        [pages.pinSuccess]: (
            <SuccessPage
                message={t('SETTINGS.PARENTAL_SUCCESSFULL')}
                buttonTitle={t('SETTINGS.ENTER_PARENTAL_CONTROLS_BTN')}
                onContinue={resetPinFinishedCallback}
            />
        )
    };

    return (
        <View style={defaultStyles.container}>
            <ModalHeader onPress={onCloseCallback} />
            {pageComponents[page]}
        </View>
    );
};

ResetPin.propTypes = {
    navigation: shape({}).isRequired
};

export default ResetPin;
